import React, { useState, useEffect } from 'react';
import TextField from 'components/FormElements/TextField';
import ButtonFilled from 'components/FormElements/ButtonFilled';
import { useTranslation } from "react-i18next";
import { PhoneInputField } from "../../../../components/PhoneInput";
import { useRegistrationForm } from '../../hooks/useRegistrationForm';
import Select from "react-dropdown-select";
import CreatableSelect from 'react-select/creatable';
import directoryApi from '../../../../store/directoryStore/directoryApi';
import { useHistory } from 'react-router-dom';
import styles from './registrationForm.module.scss';

export default function RegistrationForm({ setShowRegistration }) {
  const { formik, pending, registrationSuccess, registrationData } = useRegistrationForm();
  const { t } = useTranslation();
  const history = useHistory();

  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        const [citiesRes, categoriesRes] = await Promise.all([
          directoryApi.getCities(),
          directoryApi.getCategories()
        ]);

        // Cities response: { data: [{ id, name, lat, long }] }
        if (citiesRes && citiesRes.data && Array.isArray(citiesRes.data)) {
          setCities(citiesRes.data.map(city => ({
            label: city.name,
            value: city.id
          })));
        }

        // Categories response: { data: { timestamp, categories: [...] } }
        if (categoriesRes && categoriesRes.data) {
          const cats = categoriesRes.data.categories || categoriesRes.data;
          if (Array.isArray(cats)) {
            setCategories(cats.filter(cat => cat.active).map(cat => ({
              label: cat.name,
              value: cat.id
            })));
          }
        }

        setLoadingData(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Removed auto-redirect - user will manually navigate to auth page

  if (registrationSuccess) {
    return (
      <div className={styles.successContainer}>
        <div className={styles.successIcon}>✓</div>
        <h2>Регистрация успешна!</h2>
        <p className={styles.successMessage}>
          {registrationData?.message || 'Регистрация прошла успешно!'}
        </p>
        {registrationData?.temp_password && (
          <div className={styles.credentialsBox}>
            <p><strong>Ваши данные для входа:</strong></p>
            <p>Телефон: {registrationData.phone}</p>
            <p>Временный пароль: {registrationData.temp_password}</p>
            <p className={styles.note}>Данные также отправлены на вашу почту/SMS</p>
          </div>
        )}
        <button
          className={styles.authButton}
          onClick={() => setShowRegistration(false)}
        >
          ПЕРЕЙТИ К АВТОРИЗАЦИИ
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit} className={styles.form}>
      <div className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Контактная информация руководителя</h3>

        <div className={styles.inputGroup}>
          <PhoneInputField
            value={formik.values.phone}
            error={formik.touched.phone && formik.errors.phone}
            pending={pending || loadingData}
            onChange={(v) => formik.setFieldValue('phone', v)}
            label="Номер телефона"
          />
        </div>

        <div className={styles.inputGroup}>
          <TextField
            label="Ваше имя"
            placeholder="Введите ваше имя"
            disabled={pending || loadingData}
            value={formik.values.name}
            error={formik.touched.name && formik.errors.name}
            onChange={(e) => formik.setFieldValue('name', e.target.value)}
          />
        </div>

        <div className={styles.inputGroup}>
          <TextField
            label="Ваша фамилия"
            placeholder="Введите вашу фамилию"
            disabled={pending || loadingData}
            value={formik.values.last_name}
            error={formik.touched.last_name && formik.errors.last_name}
            onChange={(e) => formik.setFieldValue('last_name', e.target.value)}
          />
        </div>

        <div className={styles.inputGroup}>
          <TextField
            label="Email"
            placeholder="your@email.com"
            disabled={pending || loadingData}
            type="email"
            value={formik.values.email}
            error={formik.touched.email && formik.errors.email}
            onChange={(e) => formik.setFieldValue('email', e.target.value)}
          />
        </div>
      </div>

      <div className={styles.formSection}>
        <h3 className={styles.sectionTitle}>Информация о бизнесе</h3>

        <div className={styles.inputGroup}>
          <TextField
            label="Название бизнеса"
            placeholder="Бургер Финг"
            disabled={pending || loadingData}
            value={formik.values.branch_name}
            error={formik.touched.branch_name && formik.errors.branch_name}
            onChange={(e) => formik.setFieldValue('branch_name', e.target.value)}
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>Город</label>
          <Select
            options={cities}
            values={cities.filter(c => c.value === formik.values.city_id)}
            onChange={(values) => formik.setFieldValue('city_id', values[0]?.value || '')}
            placeholder="Выберите город"
            disabled={pending || loadingData}
            searchable
            className={styles.select}
          />
          {formik.touched.city_id && formik.errors.city_id && (
            <div className={styles.error}>{formik.errors.city_id}</div>
          )}
        </div>

        <div className={styles.inputGroup}>
          <TextField
            label="Адрес"
            placeholder="ул. Абая 1, офис 101"
            disabled={pending || loadingData}
            value={formik.values.address}
            error={formik.touched.address && formik.errors.address}
            onChange={(e) => formik.setFieldValue('address', e.target.value)}
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>Категории бизнеса</label>
          <CreatableSelect
            isMulti
            options={categories}
            value={formik.values.categories.map(catId => {
              const found = categories.find(c => c.value === catId);
              return found || { label: catId, value: catId };
            })}
            onChange={(selectedOptions) => {
              const values = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
              formik.setFieldValue('categories', values);
            }}
            onCreateOption={(inputValue) => {
              const newCategory = { label: inputValue, value: inputValue };
              const newCategories = [...formik.values.categories, inputValue];
              setCategories([...categories, newCategory]);
              formik.setFieldValue('categories', newCategories);
            }}
            placeholder="Выберите или введите категории"
            isDisabled={pending || loadingData}
            menuPlacement="auto"
            formatCreateLabel={(inputValue) => `Создать "${inputValue}"`}
            className={styles.reactSelect}
            classNamePrefix="react-select"
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: state.isFocused ? '#4EB78B' : '#E5E5E5',
                boxShadow: state.isFocused ? '0 0 0 3px rgba(78, 183, 139, 0.1)' : 'none',
                '&:hover': { borderColor: '#4EB78B' },
                minHeight: '44px',
                borderRadius: '8px'
              }),
              menu: (base) => ({
                ...base,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                zIndex: 9999
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#4EB78B',
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: 'white',
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: 'white',
                '&:hover': {
                  backgroundColor: '#3a9670',
                  color: 'white',
                }
              })
            }}
          />
          {formik.touched.categories && formik.errors.categories && (
            <div className={styles.error}>{formik.errors.categories}</div>
          )}
        </div>
      </div>

      <div className={styles.formActions}>
        <ButtonFilled
          type="submit"
          loading={pending}
          disabled={loadingData}
        >
          Зарегистрироваться
        </ButtonFilled>

        <button
          type="button"
          className={styles.backButton}
          onClick={() => setShowRegistration(false)}
          disabled={pending}
        >
          Назад ко входу
        </button>
      </div>

      <div className={styles.infoNote}>
        <p>
          После регистрации вы получите доступ к разделу "Акции".
          Для активации программы лояльности обратитесь в отдел продаж.
        </p>
      </div>
    </form>
  );
}
